'use client';

import { Card, CardContent } from '@packages/shared/src/ui/Card/Card';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type {
  GTMEventGlycerinButtonClick,
  GTMEventGlycerinDisplayFeature,
} from '@packages/tracking/src/types/events';
import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

export type ExtendedRecoCardItemProps = {
  /**
   * The link to the extended view page
   */
  link: string;
  /**
   * Recommendation type abbreviation, e.g. "prudsys_dv"
   */
  rdeTypeAbbr?: string;
};

export const ExtendedRecoCardItem = ({ link, rdeTypeAbbr }: ExtendedRecoCardItemProps) => {
  const { formatMessage } = useIntl();
  const dispatchEvent = useTracking();

  const onClickHandler = () => {
    dispatchEvent<GTMEventGlycerinButtonClick>({
      event: 'ButtonClick',
      ButtonClickData: {
        category: 'reco-card',
        label: 'Navi-Click',
        detail: rdeTypeAbbr,
        target: link,
      },
    });
  };

  const hasTrackedDisplayFeature = useRef(false);

  useEffect(() => {
    if (hasTrackedDisplayFeature.current) return;
    hasTrackedDisplayFeature.current = true;

    dispatchEvent<GTMEventGlycerinDisplayFeature>({
      event: 'DisplayFeature',
      DisplayFeatureData: {
        category: 'reco-card',
        label: 'Navi-Click',
        detail: rdeTypeAbbr,
        target: link,
      },
    });
    // the ref guard ensures the event fires exactly once, so the dependencies can stay honest
    // instead of being silenced with an eslint-disable
  }, [dispatchEvent, link, rdeTypeAbbr]);

  return (
    <li className="product-card">
      <Link href={link} onClick={onClickHandler} className="h-full">
        <Card className="hover:bg-color-grey-light h-full">
          <CardContent className="flex h-full flex-col items-center justify-center">
            <p className="text-center underline">
              {formatMessage({
                id: 'extendedRecoCardItem.title',
                defaultMessage: 'Entdecke mehr interessante Artikel',
              })}
            </p>
          </CardContent>
        </Card>
      </Link>
    </li>
  );
};
